import React from "react";
import PropTypes from "prop-types";
import InputGroup from "reactstrap/lib/InputGroup";
import { gatherClasses, isFunction } from "../../lib/utils";
import {
  DEFAULT_INTERVAL,
  get12HoursOptions,
  get24HoursOptions,
  parseValue,
  convertMinsToHoursLabel,
  MERIDIEM_AM
} from "../../lib/timePickerUtils";
import Meridiem from "./Meridiem";
import TimeSelect from "./TimeSelect";
import "./timePicker.scss";

const BLUR_BY_DROPDOWN = "BLUR_BY_DROPDOWN";

class TimePicker extends React.Component {
  /**
   * Constructor
   * We parse the provided timepicker value and set correct
   * Minutes and Meridiem (AM|PM)
   *
   * @param {Object} props Initial props
   */
  constructor(props) {
    super(props);
    const { value, use24Hours } = props;
    const { selectedTime, meridiem } = value
      ? parseValue(value, use24Hours)
      : { meridiem: MERIDIEM_AM, selectedTime: null };
    this.state = {
      options: this.getTimeSelectOptions(meridiem),
      originalValue: value,
      meridiem,
      selectedTime,
      inputBlurredBy: null,
      popperPosition: "fixed"
    };
    this.selectRef = React.createRef();
  }

  /**
   * Component did mount hook
   */
  componentDidMount() {
    // Special case handling of the date picker rendered inside in a filter button
    if (this.isDescendantOfPopover()) {
      this.setState({ popperPosition: "absolute" });
    }
  }

  /**
   * Check if the the select input is descendant of popover
   */
  isDescendantOfPopover() {
    let element = null;
    const popoverCls = "oui-popover";

    if (
      this.selectRef.current !== null &&
      this.selectRef.current.select &&
      this.selectRef.current.select.inputRef
    ) {
      element = this.selectRef.current.select.inputRef;
    }

    if (element !== null) {
      // eslint-disable-next-line no-cond-assign
      while ((element = element.parentNode)) {
        if (
          element &&
          element.classList &&
          element.classList.contains(popoverCls)
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * If component received a new value from user,
   * return a new state, else return current state
   *
   * @param {Object} props new props
   * @param {Object} state current state
   */
  static getDerivedStateFromProps(props, state) {
    if (props.value !== state.originalValue) {
      if (props.value) {
        const { selectedTime, meridiem } = parseValue(
          props.value,
          props.use24Hours
        );
        return {
          originalValue: props.value,
          meridiem,
          selectedTime
        };
      }

      return {
        originalValue: props.value,
        meridiem: MERIDIEM_AM,
        selectedTime: null
      };
    }
    return state;
  }

  /**
   * Get a different list of items in case of 24 hours and 12 hours.
   * Also we need to consider the interval. If its 12 hours timepicker,
   * then only half list will be shown but minutes will be based on AM|PM.
   */
  getTimeSelectOptions = meridiem => {
    const { use24Hours, interval } = this.props;
    const { meridiem: stateMeridiem } = this.state || {};

    if (use24Hours) {
      return get24HoursOptions(interval);
    }

    return get12HoursOptions(interval)(meridiem || stateMeridiem);
  };

  /**
   * Update the final minutes in state, when user selects
   * an option. Also we trigger the user provided onTimeSelect
   * handler if given.
   */
  onTimeSelect = time => {
    this.setState(
      {
        selectedTime: time,
        inputBlurredBy: BLUR_BY_DROPDOWN
      },
      () => {
        this.selectRef.current.blur();
        this.selectRef.current.focus();
        this.sendChangedValue();
      }
    );
  };

  /**
   * Update the final minutes by (+720 or -720) in case of
   * the meridiem is changed from AM to PM or PM to AM.
   * This function will not be called if use24Hours prop
   * is true as the dropdown will not be visible.
   *
   * Also we trigger the onTimeSelect if user provided one
   */
  onMerdiemChange = meridiem => {
    const { selectedTime } = this.state;

    if (!selectedTime) {
      this.setState({ meridiem });
      return;
    }

    const prevValue = selectedTime.value;
    let newValue = prevValue;

    // If mode is changed from AM to PM and previous final minutes are
    // already greater than 720 (just for safer side) then don't touch
    // it otherwise add more 720 minutes. Same for AM (minus 720)
    if (meridiem === "PM") {
      newValue = prevValue > 720 ? prevValue : prevValue + 720;
    } else {
      newValue = prevValue < 720 ? prevValue : prevValue - 720;
    }

    // Update the final minutes and meridiem
    this.setState(
      state => ({
        meridiem,
        selectedTime: {
          label: state.selectedTime.label,
          value: newValue
        }
      }),
      () => {
        this.sendChangedValue();
      }
    );
  };

  /**
   * This is called when the select text input is blurred.
   * We try to parse what user typed and if its valid input
   * as per specs, we update final minutes and meridiem else,
   * return back.
   */
  onInputBlur = parsedValue => {
    // If user succeeded to get correct time, unfold it and
    // update the state with final minutes and meridiem.
    // But only when the value has actually been changed
    const { meridiem, selectedTime } = parsedValue;
    const { inputBlurredBy, selectedTime: stateSelectedTime } = this.state;
    if (
      inputBlurredBy === null &&
      meridiem &&
      selectedTime &&
      (!stateSelectedTime ||
        (stateSelectedTime && selectedTime.value !== stateSelectedTime.value))
    ) {
      this.setState(
        {
          // eslint-disable-next-line react/no-access-state-in-setstate
          ...this.state,
          meridiem,
          selectedTime
        },
        () => {
          // Also we need to trigger onTimeSelect
          this.sendChangedValue();
        }
      );
    }

    this.setState({ inputBlurredBy: null });
  };

  /**
   * This method is used to send latest time value to
   * the consumer via onTimeSelect prop. We convert the
   * final minutes to time label as per 24 or 12hours picker.
   */
  sendChangedValue() {
    const { onTimeSelect, use24Hours } = this.props;
    if (isFunction(onTimeSelect)) {
      const { selectedTime } = this.state;
      const returnValue = selectedTime
        ? convertMinsToHoursLabel(selectedTime.value, use24Hours, true)
        : null;
      onTimeSelect.call(null, returnValue);
    }
  }

  /**
   * Clear timepicker value. If selected time is already
   * null, then don't trigger user's callback
   */
  clearTimePickerValue = () => {
    const { selectedTime } = this.state;
    if (selectedTime !== null) {
      this.setState(
        {
          selectedTime: null
        },
        () => {
          this.selectRef.current.blur();
          this.sendChangedValue();
        }
      );
    }
  };

  render() {
    const {
      isInvalid,
      disabled,
      dataTestId,
      use24Hours,
      placeholder,
      allowCustomTime
    } = this.props;
    const { selectedTime, meridiem, popperPosition } = this.state;
    return (
      <InputGroup
        data-testid={dataTestId}
        className={gatherClasses(
          this.props,
          "oui-timepicker",
          isInvalid ? "oui-is-invalid" : ""
        )}
      >
        <TimeSelect
          value={selectedTime}
          onSelect={this.onTimeSelect}
          options={this.getTimeSelectOptions()}
          use24Hours={use24Hours}
          onInputBlur={this.onInputBlur}
          selectRef={this.selectRef}
          meridiem={meridiem}
          clearTimePickerValue={this.clearTimePickerValue}
          popperPosition={popperPosition}
          disabled={disabled}
          placeholder={placeholder}
          allowCustomTime={allowCustomTime}
        />
        {!use24Hours && (
          <Meridiem
            value={meridiem}
            onChange={this.onMerdiemChange}
            disabled={disabled}
          />
        )}
      </InputGroup>
    );
  }
}

TimePicker.defaultProps = {
  value: null,
  use24Hours: false,
  interval: DEFAULT_INTERVAL,
  disabled: false,
  dataTestId: "orion-timepicker",
  placeholder: "Select Time...",
  allowCustomTime: true
};

TimePicker.propTypes = {
  use24Hours: PropTypes.bool,
  interval: PropTypes.number,
  isInvalid: PropTypes.bool,
  value: PropTypes.string,
  onTimeSelect: PropTypes.func,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
  placeholder: PropTypes.string,
  allowCustomTime: PropTypes.bool
};

TimePicker.displayName = "OrionTimePicker";

export default TimePicker;
